import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import {
  FolderSync,
  Link,
  Trash2,
  RefreshCw,
  Loader,
  CheckCircle2,
  Clock,
  Plus,
  ExternalLink,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SharePointModal({ open, onClose, sessionId, onSyncComplete }) {
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [folderUrl, setFolderUrl] = useState('');
  const [settingFolder, setSettingFolder] = useState(null); // connection_id being configured
  const [syncing, setSyncing] = useState(null); // connection_id being synced
  const [syncProgress, setSyncProgress] = useState(null);
  const [syncDone, setSyncDone] = useState(false);

  const fetchConnections = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/sharepoint/connections`);
      setConnections(data.connections || []);
    } catch (err) {
      console.error('Failed to fetch connections:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) fetchConnections();
  }, [open, fetchConnections]);

  const startOAuth = async () => {
    try {
      const { data } = await axios.post(`${API}/sharepoint/connect`);
      // Open OAuth popup
      const width = 600, height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      const popup = window.open(
        data.auth_url,
        'SharePoint Login',
        `width=${width},height=${height},left=${left},top=${top}`
      );

      // Listen for callback message
      const handler = (event) => {
        if (event.data?.type === 'sharepoint-oauth-complete') {
          window.removeEventListener('message', handler);
          popup?.close();
          fetchConnections();
          toast.success('SharePoint connected successfully');
        }
      };
      window.addEventListener('message', handler);
    } catch (err) {
      toast.error('Failed to start SharePoint connection');
    }
  };

  const setFolder = async (connectionId) => {
    if (!folderUrl.trim()) return;
    setSettingFolder(connectionId);
    try {
      const { data } = await axios.post(`${API}/sharepoint/connections/${connectionId}/set-folder`, {
        folder_url: folderUrl.trim(),
      });
      toast.success(`Folder set: ${data.folder_name}`);
      setFolderUrl('');
      fetchConnections();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to set folder');
    } finally {
      setSettingFolder(null);
    }
  };

  const syncFolder = async (connectionId) => {
    if (!sessionId) {
      toast.error('Please select a chat session first');
      return;
    }
    setSyncing(connectionId);
    setSyncProgress(null);
    setSyncDone(false);

    try {
      const response = await fetch(
        `${API}/sharepoint/connections/${connectionId}/sync?session_id=${sessionId}`,
        {
          method: 'POST',
          credentials: 'include',
        }
      );

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const data = JSON.parse(line.slice(6));
            if (data.event === 'init') {
              setSyncProgress({ completed: 0, failed: 0, total: data.total_files, eta_seconds: 0 });
            } else if (data.event === 'progress') {
              setSyncProgress({
                completed: data.completed,
                failed: data.failed,
                total: data.total,
                eta_seconds: data.eta_seconds,
              });
            } else if (data.event === 'done') {
              setSyncDone(true);
              setSyncProgress((p) => ({ ...p, completed: data.successful, failed: data.failed }));
              toast.success(`Sync complete: ${data.successful} files indexed in ${data.total_time}s`);
              fetchConnections();
              onSyncComplete?.();
            } else if (data.event === 'info') {
              toast.info(data.message);
            } else if (data.event === 'error') {
              toast.error(data.message || 'Sync failed');
            }
          } catch { /* skip */ }
        }
      }
    } catch (err) {
      toast.error('Sync failed');
      console.error('SharePoint sync error:', err);
    } finally {
      setSyncing(null);
    }
  };

  const deleteConnection = async (connectionId) => {
    if (!window.confirm('Delete this SharePoint connection?')) return;
    try {
      await axios.delete(`${API}/sharepoint/connections/${connectionId}`);
      toast.success('Connection deleted');
      fetchConnections();
    } catch (err) {
      toast.error('Failed to delete connection');
    }
  };

  const pct = syncProgress && syncProgress.total > 0
    ? Math.round(((syncProgress.completed + syncProgress.failed) / syncProgress.total) * 100)
    : 0;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FolderSync className="w-5 h-5 text-ugmonk-dark" />
            SharePoint / OneDrive
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-10">
            <Loader className="w-6 h-6 animate-spin text-ugmonk-yellow" />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Existing connections */}
            {connections.length > 0 && (
              <div className="space-y-3">
                {connections.map((conn) => (
                  <div key={conn.id} className="border rounded-lg p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {conn.connected_folder_name || 'No folder set'}
                        </p>
                        {conn.folder_url && (
                          <a
                            href={conn.folder_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-ugmonk-yellow hover:underline flex items-center gap-1 truncate"
                          >
                            <ExternalLink className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate">{conn.folder_url}</span>
                          </a>
                        )}
                        {conn.last_synced_at && (
                          <p className="text-xs text-gray-400 mt-0.5">
                            Last synced: {new Date(conn.last_synced_at).toLocaleString()}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                        {conn.folder_id && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => syncFolder(conn.id)}
                            disabled={syncing === conn.id}
                            className="text-xs"
                          >
                            {syncing === conn.id ? (
                              <Loader className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <RefreshCw className="w-3.5 h-3.5" />
                            )}
                            <span className="ml-1">Sync</span>
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteConnection(conn.id)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>

                    {/* Set folder URL (if not set) */}
                    {!conn.folder_id && (
                      <div className="flex gap-2">
                        <Input
                          value={folderUrl}
                          onChange={(e) => setFolderUrl(e.target.value)}
                          placeholder="Paste SharePoint folder URL..."
                          className="text-sm h-8"
                        />
                        <Button
                          size="sm"
                          onClick={() => setFolder(conn.id)}
                          disabled={settingFolder === conn.id || !folderUrl.trim()}
                          className="bg-ugmonk-yellow hover:bg-[#f5cc6a] text-black h-8 text-xs"
                        >
                          {settingFolder === conn.id ? (
                            <Loader className="w-3 h-3 animate-spin" />
                          ) : (
                            <Link className="w-3 h-3" />
                          )}
                          <span className="ml-1">Set</span>
                        </Button>
                      </div>
                    )}

                    {/* Sync progress */}
                    {syncing === conn.id && syncProgress && (
                      <div className="space-y-2 pt-2 bg-[#fffdf0] rounded-lg p-3">
                        <Progress value={pct} className="h-2" />
                        <div className="flex items-center justify-between text-sm">
                          <span className="font-medium text-gray-700">
                            Processing {syncProgress.completed + syncProgress.failed}/{syncProgress.total} files
                          </span>
                          {!syncDone && syncProgress.eta_seconds > 0 && (
                            <span className="flex items-center gap-1 text-ugmonk-dark font-medium">
                              <Clock className="w-3.5 h-3.5" />
                              {syncProgress.eta_seconds >= 60
                                ? `${Math.floor(syncProgress.eta_seconds / 60)}m ${Math.ceil(syncProgress.eta_seconds % 60)}s remaining`
                                : `${Math.ceil(syncProgress.eta_seconds)}s remaining`
                              }
                            </span>
                          )}
                          {syncDone && (
                            <span className="flex items-center gap-1 text-green-600 font-medium">
                              <CheckCircle2 className="w-3.5 h-3.5" />
                              Done
                            </span>
                          )}
                        </div>
                        {syncProgress.failed > 0 && (
                          <p className="text-xs text-red-500">{syncProgress.failed} file(s) failed</p>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {connections.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <FolderSync className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                <p className="text-sm">No SharePoint folders connected</p>
                <p className="text-xs mt-1">Connect a folder to sync documents into your chat</p>
              </div>
            )}

            <Button
              onClick={startOAuth}
              className="w-full bg-ugmonk-yellow hover:bg-[#f5cc6a] text-black"
            >
              <Plus className="w-4 h-4 mr-2" />
              Connect New SharePoint Folder
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
